import { FC, useState } from 'react';
import { Listbox } from '@headlessui/react';
import { ReactComponent as ChevronDownIcon } from 'assets/icons/chevron-down.svg';
import TotalProductionChart from './TotalProductionChart/TotalProductionChart';
import TotalAppointmentRequestChart from './TotalAppointmentRequestChart/TotalAppointmentRequestChart';
import TotalShowRateChart from './TotalShowRateChart/TotalShowRateChart';
import { APPOINTMENT_STATUS } from 'utils/constants';
import { ReactComponent as EmptyBucket } from 'assets/icons/empty-bucket.svg';
import TotalPatientChart from './TotalPatientChart/TotalPatientChart';
import useTotalProduction from 'hooks/useTotalProduction';

const METRIC_OPTIONS = [
  { label: 'Appts Requested', value: undefined },
  { label: 'Shows', value: APPOINTMENT_STATUS.COMPLETED.KEY },
  { label: 'Patients', value: 'total_patients' },
  { label: 'Production', value: 'total_production' },
];

interface TotalProductionProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const TotalProduction: FC<TotalProductionProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const [selectedMetric, setSelectedMetric] = useState(METRIC_OPTIONS[3]);

  const { data: totalProduction, isLoading: isProductionLoading } =
    useTotalProduction({
      startDate: selectedDateRange.fromDate,
      endDate: selectedDateRange.toDate,
      hourType,
      clinicIds,
    });

  const isMetricAppointment = selectedMetric === METRIC_OPTIONS[0];
  // selectedMetric === METRIC_OPTIONS[1];

  return (
    <div className="px-3.2 py-2.4 shadow-elevation-06-hover bg-white h-full min-h-[44rem] flex flex-col gap-y-1.6">
      <Listbox value={selectedMetric} onChange={setSelectedMetric}>
        {({ open }) => (
          <div className="relative">
            <Listbox.Button className="flex items-center gap-x-0.8 cursor-pointer outline-none">
              <span className="text-16 font-bold leading-[2.4rem]">
                {selectedMetric.label}
              </span>
              <ChevronDownIcon className="w-1.4 h-1.4" />
            </Listbox.Button>
            {open && (
              <Listbox.Options className="absolute mt-0.5 bg-white rounded-[1rem] shadow-primary z-10 py-1.5 flex flex-col gap-y-1.5 w-max outline-none max-h-[20rem] overflow-y-auto">
                {METRIC_OPTIONS.map((option) => (
                  <Listbox.Option
                    key={option.label}
                    value={option}
                    className={({ active, selected }) =>
                      `cursor-pointer pl-1.6 pr-2.4 ${
                        active || selected ? 'text-magenta' : ''
                      }`
                    }
                  >
                    {option.label}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            )}
          </div>
        )}
      </Listbox>

      {selectedMetric === METRIC_OPTIONS[3] &&
        (totalProduction || isProductionLoading ? (
          <TotalProductionChart
            selectedDateRange={selectedDateRange}
            hourType={hourType}
            totalProductionData={totalProduction}
            isLoading={isProductionLoading}
          />
        ) : (
          <div className="flex-1 flex items-center flex-col justify-center gap-y-1">
            <EmptyBucket />
            <span>
              Production reporting is unavailable for this PMS provider.
            </span>
          </div>
        ))}

      {isMetricAppointment && (
        <TotalAppointmentRequestChart
          selectedDateRange={selectedDateRange}
          hourType={hourType}
          status={selectedMetric.value}
          clinicIds={clinicIds}
        />
      )}

      {selectedMetric === METRIC_OPTIONS[1] && (
        <TotalShowRateChart
          selectedDateRange={selectedDateRange}
          hourType={hourType}
          clinicIds={clinicIds}
        />
      )}

      {selectedMetric === METRIC_OPTIONS[2] && (
        <TotalPatientChart
          selectedDateRange={selectedDateRange}
          hourType={hourType}
          clinicIds={clinicIds}
        />
      )}
    </div>
  );
};

export default TotalProduction;
